import React, { useCallback, useEffect, useMemo } from "react";
import { AccountsProjectedBalance } from "@ui-kit/components/index.es";
import { connect } from "react-redux";
import { compose } from "redux";
import { push } from "react-router-redux";
import { bool, func, shape, string } from "prop-types";

import PageLoading from "pages/_components/PageLoading";
import { resizableRoute } from "pages/_components/Resizable";

import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import { actions as layoutActions } from "reducers/layout";
import { selectors as sessionSelectors } from "reducers/session";

import { maskedAccountNumber, productTypes } from "util/accounts";
import { getIsoCurrency } from "util/deposits";
import * as i18n from "util/i18n";
import { dispatchV3ShellLayout, isUxDashboardImprovementsEnabled } from "util/v3ShellLayoutSync";
import { getBankAccountDetailPath } from "util/v3UxConfig";
import {
    buildDeferredMovementsPagination,
    buildProjectedBalanceSummary,
    extractDeferredMovementsList,
    mapDeferredMovementToRow,
} from "v3/pages/accounts/accountProjectedBalance";
import { getAccountDetailLabels, getProjectedBalanceLabels, getProjectedBalanceSummaryLabels } from "v3/v3UiKitI18n";

function AccountProjectedBalanceV3Page(props) {
    AccountProjectedBalanceV3Page.propTypes = {
        dispatch: func.isRequired,
        isDesktop: bool.isRequired,
        account: shape({}),
        fetching: bool.isRequired,
        loggedUser: shape({}).isRequired,
        match: shape({
            params: shape({
                id: string,
            }),
        }).isRequired,
        proyectedBalances: shape({}),
        diferedMovements: shape({}),
    };

    AccountProjectedBalanceV3Page.defaultProps = {
        account: null,
        proyectedBalances: null,
        diferedMovements: null,
    };

    const { dispatch, isDesktop, account, fetching, loggedUser, match, proyectedBalances, diferedMovements } = props;

    const accountId = match?.params?.id;
    const goBackPath = accountId != null ? getBankAccountDetailPath(accountId) : "/v3/accounts";
    const currencyIso = account ? getIsoCurrency(String(account.currency)) : "ARS";
    const balance = proyectedBalances?.[0] ?? null;

    useEffect(() => {
        if (!accountId || !loggedUser?.userId) {
            return undefined;
        }
        dispatch(accountsActions.details(accountId, loggedUser.userId));
        return undefined;
    }, [dispatch, accountId, loggedUser?.userId]);

    useEffect(() => {
        if (!account?.idProduct) {
            return undefined;
        }
        dispatch(accountsActions.proyectedBalance(account.idProduct));
        dispatch(accountsActions.diferedMovements(account.idProduct, "1"));
        return undefined;
    }, [dispatch, account?.idProduct]);

    const handleBack = useCallback(() => {
        dispatch(push(goBackPath));
    }, [dispatch, goBackPath]);

    const handleDeferredMovementsPage = useCallback(
        (pageNumber) => {
            if (!account?.idProduct) {
                return;
            }
            dispatch(accountsActions.diferedMovements(account.idProduct, pageNumber));
        },
        [account?.idProduct, dispatch],
    );

    const accountLabel = account ? account.productAlias || maskedAccountNumber(account.number) : "";
    const title =
        account && isDesktop
            ? `${i18n.get("proyected.balance.title.to")} ${accountLabel}`
            : i18n.get("proyected.balance.title");
    const subtitle = account
        ? `${i18n.get(`accounts.productType.${productTypes[account.productType]}`)} Nº ${maskedAccountNumber(
              account.number,
          )}`
        : "";

    const summary = useMemo(
        () => buildProjectedBalanceSummary(balance, currencyIso, getProjectedBalanceSummaryLabels),
        [balance, currencyIso],
    );

    const movementRows = useMemo(() => {
        const list = extractDeferredMovementsList(diferedMovements);
        return list.map((movement) => mapDeferredMovementToRow(movement, currencyIso));
    }, [currencyIso, diferedMovements]);

    const listConfig = useMemo(
        () => ({
            emptyStateText: getProjectedBalanceLabels("empty"),
            paginationProps: buildDeferredMovementsPagination(diferedMovements, handleDeferredMovementsPage),
        }),
        [diferedMovements, handleDeferredMovementsPage],
    );

    useEffect(() => {
        if (!isUxDashboardImprovementsEnabled() || !account || isDesktop) {
            return undefined;
        }
        dispatchV3ShellLayout(dispatch, {
            headerTitle: title,
            headerSubtitle: subtitle,
            goBackPath,
            rightContentMobile: null,
            scopes: ["accounts", "account/details"],
        });
        return () => {
            dispatch(layoutActions.setScopes([]));
            dispatch(layoutActions.setTitleKey(""));
            dispatch(layoutActions.setHeaderTitle(""));
            dispatch(layoutActions.setHeaderSubtitle(""));
            dispatch(layoutActions.setGoBackPath(null));
            dispatch(layoutActions.setRightContentMobile(null));
        };
    }, [dispatch, account, isDesktop, title, subtitle, goBackPath]);

    const isLoading = fetching || !account || !proyectedBalances || !diferedMovements;

    return (
        <PageLoading loading={isLoading} className="screen-loader">
            {!isLoading && account && summary && (
                <AccountsProjectedBalance
                    goBack={handleBack}
                    title={title}
                    subtitle={subtitle}
                    getMovementCardLabels={getProjectedBalanceLabels}
                    summary={summary}
                    showBalanceChartLabel={getAccountDetailLabels("showBalanceChart")}
                    hideBalanceChartLabel={getAccountDetailLabels("hideBalanceChart")}
                    list={listConfig}
                    rows={movementRows}
                />
            )}
        </PageLoading>
    );
}

const mapStateToProps = (state) => ({
    loggedUser: sessionSelectors.getUser(state),
    account: accountsSelectors.getSelectedAccount(state),
    proyectedBalances: accountsSelectors.getProyectedBalances(state),
    diferedMovements: accountsSelectors.getDiferedMovements(state),
    fetching: accountsSelectors.getFetching(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(AccountProjectedBalanceV3Page);
